import { useState } from 'react'
import { createRootRoute, Outlet } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { Navbar } from '@/components/layout/navbar'
import { QueueDrawer } from '@/components/layout/queue-drawer'
import { Toaster } from 'sonner'
import { useProactiveRefresh, api, useAuth } from '@/lib/api'
import '@/styles/globals.css'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: (failureCount, error: any) => {
        if (error?.status === 401) return false
        return failureCount < 3
      },
    },
  },
})

export const Route = createRootRoute({
  component: RootLayout,
})

function RootLayout() {
  const [queueOpen, setQueueOpen] = useState(false)

  return (
    <QueryClientProvider client={queryClient}>
      <RootContent queueOpen={queueOpen} setQueueOpen={setQueueOpen} />
    </QueryClientProvider>
  )
}

function RootContent({ queueOpen, setQueueOpen }: { queueOpen: boolean; setQueueOpen: React.Dispatch<React.SetStateAction<boolean>> }) {
  useProactiveRefresh()
  const { isAuthenticated } = useAuth()

  const { data: queueData } = useQuery({
    queryKey: ['queue', 'updates'],
    queryFn: api.queue.updates,
    enabled: isAuthenticated,
    refetchInterval: (query) => {
      if (!isAuthenticated) return false
      const hasItems = query.state.data?.queue && query.state.data.queue.length > 0
      return hasItems ? 3000 : false
    },
    refetchOnWindowFocus: isAuthenticated,
  })

  const queueCount = queueData?.queue?.length ?? 0

  return (
    <div className="min-h-screen bg-background">
      <Navbar
        onToggleQueue={() => setQueueOpen((prev) => !prev)}
        isQueueOpen={queueOpen}
        queueCount={queueCount}
      />
      <main className={`pb-10 transition-all duration-200 ease-in-out ${queueOpen ? 'pr-80' : ''}`}>
        <Outlet />
      </main>
      <QueueDrawer isOpen={queueOpen} onClose={() => setQueueOpen(false)} />
      <Toaster position="bottom-right" />
    </div>
  )
}
